/**
 * Convert one or more images into a multi-page PDF (A4, contain-fit).
 */

import { PDFDocument, rgb } from '@cantoo/pdf-lib';

/** A4 portrait in PDF points */
export const A4_WIDTH_PTS = 595.28;
export const A4_HEIGHT_PTS = 841.89;

export const MAX_IMAGE_PAGES = 20;
export const MAX_TOTAL_IMAGE_BYTES = 15 * 1024 * 1024;

export interface ImagePdfPage {
  blob: Blob;
  name?: string;
}

export interface ImagesToPdfOptions {
  pageWidthPts?: number;
  pageHeightPts?: number;
}

function blobToUint8(blob: Blob): Promise<Uint8Array> {
  return blob.arrayBuffer().then((buf) => new Uint8Array(buf));
}

function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      if (!img.naturalWidth || !img.naturalHeight) {
        reject(new Error('Image is empty'));
        return;
      }
      resolve(img);
    };
    img.onerror = () => reject(new Error('Failed to decode image'));
    img.src = src;
  });
}

/** Rasterize any browser-decodable image to PNG (WebP / HEIC fallback path). */
async function rasterizeToPngBlob(blob: Blob): Promise<Blob> {
  const url = URL.createObjectURL(blob);
  try {
    const img = await loadImageElement(url);
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas unavailable');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);
    const png = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('Failed to export PNG'))),
        'image/png'
      );
    });
    return png;
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function embedImage(
  pdfDoc: PDFDocument,
  blob: Blob
): Promise<{ width: number; height: number; draw: (page: ReturnType<PDFDocument['addPage']>, x: number, y: number, w: number, h: number) => void }> {
  const type = (blob.type || '').toLowerCase();
  const bytes = await blobToUint8(blob);

  const tryJpg = async () => {
    const img = await pdfDoc.embedJpg(bytes);
    return {
      width: img.width,
      height: img.height,
      draw: (
        page: ReturnType<PDFDocument['addPage']>,
        x: number,
        y: number,
        w: number,
        h: number
      ) => page.drawImage(img, { x, y, width: w, height: h }),
    };
  };

  const tryPng = async () => {
    const img = await pdfDoc.embedPng(bytes);
    return {
      width: img.width,
      height: img.height,
      draw: (
        page: ReturnType<PDFDocument['addPage']>,
        x: number,
        y: number,
        w: number,
        h: number
      ) => page.drawImage(img, { x, y, width: w, height: h }),
    };
  };

  if (type.includes('jpeg') || type.includes('jpg')) {
    try {
      return await tryJpg();
    } catch {
      /* fall through */
    }
  }

  if (type.includes('png')) {
    try {
      return await tryPng();
    } catch {
      /* fall through */
    }
  }

  // Unknown / WebP / HEIC / failed native embed — rasterize to PNG
  try {
    if (type.includes('jpeg') || type.includes('jpg') || bytes[0] === 0xff) {
      return await tryJpg();
    }
    if (type.includes('png') || (bytes[0] === 0x89 && bytes[1] === 0x50)) {
      return await tryPng();
    }
  } catch {
    /* rasterize */
  }

  const pngBlob = await rasterizeToPngBlob(blob);
  const pngBytes = await blobToUint8(pngBlob);
  const img = await pdfDoc.embedPng(pngBytes);
  return {
    width: img.width,
    height: img.height,
    draw: (page, x, y, w, h) => page.drawImage(img, { x, y, width: w, height: h }),
  };
}

function containRect(
  imgW: number,
  imgH: number,
  pageW: number,
  pageH: number
): { x: number; y: number; width: number; height: number } {
  const scale = Math.min(pageW / imgW, pageH / imgH);
  const width = imgW * scale;
  const height = imgH * scale;
  return {
    x: (pageW - width) / 2,
    y: (pageH - height) / 2,
    width,
    height,
  };
}

/**
 * Build a PDF from image blobs. One page per image, A4 portrait by default.
 */
export async function imagesToPdf(
  pages: ImagePdfPage[],
  opts: ImagesToPdfOptions = {}
): Promise<ArrayBuffer> {
  if (!pages.length) {
    throw new Error('Add at least one image');
  }
  if (pages.length > MAX_IMAGE_PAGES) {
    throw new Error(`Too many pages (max ${MAX_IMAGE_PAGES})`);
  }

  let totalBytes = 0;
  for (const p of pages) {
    totalBytes += p.blob.size;
  }
  if (totalBytes > MAX_TOTAL_IMAGE_BYTES) {
    throw new Error('Images are too large combined (max 15 MB). Use fewer or smaller photos.');
  }

  const pageW = opts.pageWidthPts ?? A4_WIDTH_PTS;
  const pageH = opts.pageHeightPts ?? A4_HEIGHT_PTS;

  const pdfDoc = await PDFDocument.create();

  for (let i = 0; i < pages.length; i++) {
    const page = pdfDoc.addPage([pageW, pageH]);
    page.drawRectangle({
      x: 0,
      y: 0,
      width: pageW,
      height: pageH,
      color: rgb(1, 1, 1),
    });

    try {
      const embedded = await embedImage(pdfDoc, pages[i].blob);
      const box = containRect(embedded.width, embedded.height, pageW, pageH);
      embedded.draw(page, box.x, box.y, box.width, box.height);
    } catch (err) {
      const label = pages[i].name || `Image ${i + 1}`;
      throw new Error(
        `Could not add “${label}”: ${err instanceof Error ? err.message : 'unsupported image'}`
      );
    }
  }

  const saved = await pdfDoc.save();
  const copy = new Uint8Array(saved.byteLength);
  copy.set(saved);
  return copy.buffer;
}

/** Suggest a download/open name from the first image. */
export function suggestPdfNameFromImages(pages: ImagePdfPage[], fallback = 'Scan.pdf'): string {
  const first = pages[0]?.name?.trim();
  if (!first) return fallback;
  const base = first.replace(/\.[^.]+$/, '') || 'Scan';
  return `${base}.pdf`;
}
